/**
 * Login flow - multi-step authentication with SSO and password options.
 *
 * NOTE: Browser password managers (Bitwarden, LastPass, 1Password, etc.) may
 * inject attributes into form elements, producing harmless hydration warnings.
 */

import type {
  OnGetSsoAuthorizationUrl,
  OnGetSsoConnections,
  OnPasswordAuth,
  OnResetPassword,
  OnSendPasswordReset,
  OnVerifyMagicAuth,
} from '#lib/auth-handlers'

import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@moment/design-system-ui/lib/cn'
import { useCallback, useMemo, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button, ButtonVariant } from '#components/controls/button'
import { EmailInput } from '#components/controls/email'
import { ErrorText } from '#components/controls/input'
import { PasswordInput } from '#components/controls/password'
import { MagicAuthCode } from '#components/flows/magic-auth/magic-auth-code'
import { PasswordReset } from '#components/flows/password-reset/password-reset'
import {
  BackButton,
  ButtonDivider,
  CtaContainer,
  InputsContainer,
} from '#components/shared/styles'
import { PageMode } from '#lib/page-mode'

import { ResetSuccessMessage } from './reset-success-message'

function getDomainFromEmail(email: string): string {
  return email.split('@').pop() || ''
}

const loginFormSchema = z.object({
  email: z
    .string()
    .email({ message: 'Please enter valid email address' })
    .min(1, { message: 'Email is required' }),
  password: z.string().optional(),
})

interface LoginFormInputs {
  email: string
  password?: string
}

export interface LoginProps {
  // Auth side-effect handlers (host-provided)
  onPasswordAuth: OnPasswordAuth
  onVerifyMagicAuth: OnVerifyMagicAuth
  onSendPasswordReset: OnSendPasswordReset
  onResetPassword: OnResetPassword
  onGetSsoConnections: OnGetSsoConnections
  onGetSsoAuthorizationUrl: OnGetSsoAuthorizationUrl

  // Navigation side-effects (host performs the actual navigation).
  // redirectTo, when present, is a trusted backend-supplied destination the
  // host should honor verbatim; when absent the host picks its own fallback.
  onLoginSuccess: (redirectTo?: string) => void
  onSsoRedirect: (url: string) => void
  onClearResetToken?: () => void

  // Mode + success notifications
  onModeChange?: (mode: PageMode) => void
  onResetSuccess?: () => void
  showResetSuccess?: boolean

  // URL-derived values, supplied by the host
  resetToken?: string
  resetEmail?: string
  returnUrl?: string

  // Callback URL used to build the SSO return URL (path or absolute URL)
  ssoCallbackUrl?: string
}

export const Login = ({
  onPasswordAuth,
  onVerifyMagicAuth,
  onSendPasswordReset,
  onResetPassword,
  onGetSsoConnections,
  onGetSsoAuthorizationUrl,
  onLoginSuccess,
  onSsoRedirect,
  onClearResetToken,
  onModeChange,
  onResetSuccess,
  showResetSuccess,
  resetToken,
  resetEmail,
  returnUrl,
  ssoCallbackUrl,
}: LoginProps) => {
  const [showPasswordReset, setShowPasswordReset] = useState(
    Boolean(resetToken),
  )

  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [isFetchingOrganization, setIsFetchingOrganization] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false)
  const [hasCheckedOrganization, setHasCheckedOrganization] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  const [showMagicAuth, setShowMagicAuth] = useState(false)

  const {
    formState: { errors },
    handleSubmit,
    register,
    trigger,
    clearErrors,
    setError,
    watch,
    setValue,
    getValues,
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
  })

  const watchedEmail = watch('email')

  // STEP 1: Email validation for the Continue button
  const emailIsValid = useMemo(() => {
    try {
      loginFormSchema.shape.email.parse(watchedEmail)
      return true
    } catch {
      return false
    }
  }, [watchedEmail])

  // STEP 2: State flags for UI logic
  const showEmailOnly = !(
    hasCheckedOrganization ||
    isFetchingOrganization ||
    showMagicAuth
  )
  const showLoading = isFetchingOrganization
  const showLoginOptions =
    hasCheckedOrganization && !isFetchingOrganization && !showMagicAuth

  // STEP 3: Button logic
  const showSSOButton = showLoginOptions && Boolean(organizationId)
  const showPassword = showLoginOptions

  const handleShowPasswordReset = useCallback(() => {
    setShowPasswordReset(true)
    onModeChange?.(PageMode.ResetRequest)
  }, [onModeChange])

  const handleBackToLogin = useCallback(
    (withSuccess = false) => {
      // Clear URL parameters first if present
      if (resetToken) {
        onClearResetToken?.()
      }

      setShowPasswordReset(false)
      onModeChange?.(PageMode.Login)

      if (withSuccess) {
        onResetSuccess?.()
      }
    },
    [resetToken, onClearResetToken, onModeChange, onResetSuccess],
  )

  const handleDetermineLoginOptions = useCallback(async () => {
    const email = getValues('email')
    if (!email) {
      return
    }

    const domain = getDomainFromEmail(email)
    if (!domain) {
      setEmailError('Invalid email format')
      return
    }

    setEmailError(null)
    setIsFetchingOrganization(true)

    try {
      const responseData = await onGetSsoConnections({ domain })

      if (responseData.error) {
        throw new Error(responseData.error)
      }

      if (
        responseData.connectionTypes.length === 0 ||
        !responseData.organizationId
      ) {
        setOrganizationId(null)
      } else {
        setOrganizationId(responseData.organizationId)
      }

      setHasCheckedOrganization(true)
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error fetching SSO connections'
      setEmailError(errorMessage)
    } finally {
      setIsFetchingOrganization(false)
    }
  }, [getValues, onGetSsoConnections])

  const handleSSOLogin = useCallback(async () => {
    setSsoError(null)
    if (!organizationId) {
      return
    }
    try {
      const callbackUrl = new URL(
        ssoCallbackUrl ?? '/auth/callback',
        window.location.origin,
      )
      if (returnUrl) {
        callbackUrl.searchParams.set('next', returnUrl)
      }
      const responseData = await onGetSsoAuthorizationUrl({
        organization: organizationId,
        returnUrl: callbackUrl.toString(),
      })

      if (responseData.error) {
        throw new Error(responseData.error)
      }

      if (!responseData.authorizationUrl) {
        throw new Error('No authorization URL received from SSO service')
      }

      onSsoRedirect(responseData.authorizationUrl)
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      setSsoError(`Error initializing SSO: ${errorMessage}`)
    }
  }, [
    organizationId,
    onGetSsoAuthorizationUrl,
    onSsoRedirect,
    returnUrl,
    ssoCallbackUrl,
  ])

  const handlePasswordLogin: SubmitHandler<LoginFormInputs> = useCallback(
    async (formData) => {
      if (!formData.password) {
        setError('password', { message: 'Password is required' })
        return
      }

      setIsSubmittingPassword(true)
      try {
        const authResponse = await onPasswordAuth({
          email: formData.email,
          password: formData.password,
        })

        if (authResponse.success) {
          if (authResponse.requiresMagicAuth) {
            setShowMagicAuth(true)
            onModeChange?.(PageMode.MagicAuth)
          } else {
            // Direct login without magic auth verification.
            onLoginSuccess(authResponse.redirectTo)
          }
        } else {
          const errorMessage = authResponse.error || 'Login failed'
          setError('root.serverError', {
            message: errorMessage,
          })
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        setError('root.serverError', { message: errorMessage })
      } finally {
        setIsSubmittingPassword(false)
      }
    },
    [setError, onModeChange, onPasswordAuth, onLoginSuccess],
  )

  const handleBackFromMagicAuth = useCallback(() => {
    setShowMagicAuth(false)
    onModeChange?.(PageMode.Login)
  }, [onModeChange])

  const handleReset = useCallback(() => {
    setValue('password', '')
    clearErrors()
    setHasCheckedOrganization(false)
    setOrganizationId(null)
    setSsoError(null)
    setIsSubmittingPassword(false)
    setEmailError(null)
    setShowMagicAuth(false)
    onModeChange?.(PageMode.Login)
  }, [setValue, clearErrors, onModeChange])

  const handleFormSubmit: SubmitHandler<LoginFormInputs> = useCallback(
    async (formData) => {
      if (showEmailOnly) {
        await handleDetermineLoginOptions()
      } else if (showPassword) {
        await handlePasswordLogin(formData)
      }
    },
    [
      showEmailOnly,
      showPassword,
      handleDetermineLoginOptions,
      handlePasswordLogin,
    ],
  )

  // Show password reset flow if in reset mode
  if (showPasswordReset) {
    return (
      <PasswordReset
        onSendPasswordReset={onSendPasswordReset}
        onResetPassword={onResetPassword}
        onBackToLogin={handleBackToLogin}
        onModeChange={onModeChange}
        initialEmail={watchedEmail}
        resetEmail={resetEmail}
        resetToken={resetToken}
      />
    )
  }

  return (
    <>
      <ResetSuccessMessage showSuccess={showResetSuccess} />

      <form
        className={cn('w-full', 'flex flex-col items-center')}
        onSubmit={handleSubmit(handleFormSubmit)}
        aria-label="Sign in form"
        // we handle email validation with zod, but password validation is done on form submit
        noValidate={!showPassword}
      >
        {isFetchingOrganization && (
          <div aria-live="polite" className="sr-only">
            Checking organization...
          </div>
        )}

        <InputsContainer>
          {showMagicAuth ? (
            <MagicAuthCode
              onVerifyMagicAuth={onVerifyMagicAuth}
              onSuccess={() => {
                // No trusted backend redirect here; let the host resolve its
                // own fallback (the sanitized returnUrl param).
                onLoginSuccess()
              }}
              email={getValues('email')}
              disabled={false}
            />
          ) : (
            <>
              {/* Email input - always visible */}
              <EmailInput
                // Force re-render to clear autofill styles
                key={showLoginOptions ? 'disabled' : 'enabled'}
                register={register('email')}
                error={errors.email}
                emailError={emailError}
                disabled={showLoginOptions}
                onFocus={() => {
                  clearErrors('email')
                }}
              />

              {/* Password input - step 2 */}
              {showPassword && (
                <PasswordInput
                  register={register('password')}
                  disabled={isSubmittingPassword}
                  error={errors.password}
                  serverError={errors.root?.serverError?.message}
                  onFocus={() => {
                    clearErrors('password')
                  }}
                  onBlur={() => trigger('password')}
                  onForgotPasswordClick={handleShowPasswordReset}
                  showForgotPassword
                  autoComplete="current-password"
                />
              )}
            </>
          )}
        </InputsContainer>

        {!showMagicAuth && (
          <CtaContainer>
            {(showEmailOnly || showLoading) && (
              <Button
                label="Continue"
                onClick={handleDetermineLoginOptions}
                disabled={!emailIsValid || showLoading}
                isLoading={showLoading}
                data-testid="continue-button"
                type="button"
                className="w-full"
                variant={ButtonVariant.Secondary}
              />
            )}

            {showPassword && (
              <Button
                label="Sign in"
                isLoading={isSubmittingPassword}
                disabled={isSubmittingPassword}
                data-testid="password-button"
                type="submit"
                variant={
                  showSSOButton
                    ? ButtonVariant.Secondary
                    : ButtonVariant.Primary
                }
              />
            )}

            {showPassword && showSSOButton && <ButtonDivider />}

            {showSSOButton && (
              <Button
                label="Sign in with SSO"
                onClick={handleSSOLogin}
                data-testid="sso-button"
                type="button"
                variant={ButtonVariant.Primary}
              />
            )}

            {ssoError && <ErrorText error={ssoError} className="mt-1.5" />}
          </CtaContainer>
        )}

        {(showLoginOptions || showMagicAuth) && (
          <BackButton
            onClick={showMagicAuth ? handleBackFromMagicAuth : handleReset}
            data-testid="go-back-button"
            label={showMagicAuth ? 'Back to sign in' : 'Back to email'}
          />
        )}
      </form>
    </>
  )
}
